import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from './auth.service';
import { LocalStorageService } from './local-storage.service';
import { Operator } from '../../../Models/backoffice/operator.model';
import { Language } from '../Types/language.type';

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  private readonly _translateService = inject(TranslateService);
  private readonly _authService = inject(AuthService);
  private readonly _localStorage = inject(LocalStorageService);

  private readonly LANGUAGE_KEY = 'preferred_lang';

  readonly currentLanguage = signal<Language>('en');

  constructor() {
    this._registerOperatorSync();
  }

  resolveColdLoadLanguage(): Language {
    const stored = this._localStorage.getItem(this.LANGUAGE_KEY);

    if (this._isLanguage(stored)) {
      return stored;
    }

    return this._browserLanguage();
  }

  use(lang: Language): void {
    this._translateService.use(lang).subscribe({
      next: () => {
        this.currentLanguage.set(lang);
        this._localStorage.setItem(this.LANGUAGE_KEY, lang);
      },
    });
  }

  instant(key: string | string[], params?: object): string {
    return this._translateService.instant(key, params);
  }

  private _registerOperatorSync(): void {
    this._authService.currentOperator$.subscribe((operator) =>
      this._applyOperatorLanguage(operator),
    );
  }

  private _applyOperatorLanguage(operator: Operator | null): void {
    const preferred = operator?.preferred_lang ?? null;
    const next = this._isLanguage(preferred) ? preferred : this.resolveColdLoadLanguage();

    if (next !== this.currentLanguage()) {
      this.use(next);
    }
  }

  private _browserLanguage(): Language {
    const browser = this._translateService.getBrowserLang();

    return browser?.startsWith('fr') ? 'fr' : 'en';
  }

  private _isLanguage(value: string | null): value is Language {
    return value === 'en' || value === 'fr';
  }
}
